import moment from 'moment';

/**
 * The contractor roster and the pay calendar it runs on.
 *
 * Kept out of the page component so other pages (the Accounting clawback dialog)
 * can post into a pay period without importing the whole payment page.
 */

export const SETTINGS_DOC = 'app-config/contractor-payment-settings';

// Default contractor configuration — saved to Firestore on first use via Settings modal.
// `endDate` retires a contractor: they still appear (read-only) on periods up to and
// including that date, and are omitted from every period after it. Retiring rather
// than deleting is deliberate — this page only iterates the roster, so removing an
// entry would leave their contractor-payment-records intact but unreachable.
export const DEFAULT_CONTRACTORS = [
  { id: '2', name: 'Alan Tanski', type: 'flat', flatRate: 10000 },
  { id: '5', name: 'Max Tanski', type: 'flat', flatRate: 10000 },
  { id: '1', name: 'Ryan Tanski', type: 'sales' },
  { id: '3', name: 'Shane Hale', type: 'sales' },
  { id: '6', name: 'Kurt Johnston', type: 'service', endDate: '2026-08-16' },
  { id: '9', name: 'Superior Auto Detailing', type: 'vendor', vendorName: 'Superior Auto Detailing' },
  { id: '8', name: 'Duddy', type: 'flat', flatRate: 1600 },
  { id: '4', name: 'Mark Tanski', type: 'sales', endDate: '2026-08-16' },
  { id: '7', name: 'Automotive Architects', type: 'vendor', vendorName: 'Automotive Architects' },
];

// A retired contractor is hidden from periods after their end date. On or before it
// they still render, so historical pay stays viewable and printable.
export function isRetiredForPeriod(contractor, periodStr) {
  if (!contractor?.endDate) return false;
  return periodStr > contractor.endDate;
}

// Contractors are paid on the 1st and the 16th. The period containing today is the
// most recent of those two dates that has already arrived.
export function getDefaultPaymentDate() {
  const today = moment();
  return (today.date() >= 16 ? today.clone().date(16) : today.clone().date(1)).toDate();
}

export function getDefaultPeriod() {
  return moment(getDefaultPaymentDate()).format('YYYY-MM-DD');
}

export function isPaymentDate(date) {
  const day = moment(date).date();
  return day === 1 || day === 16;
}

// One step forward (dir > 0) or back (dir < 0) along the 1st/16th calendar.
export function stepPaymentDate(date, dir) {
  const m = moment(date);
  return m.date() === 1
    ? (dir < 0 ? m.clone().subtract(1, 'month').date(16) : m.clone().date(16))
    : (dir < 0 ? m.clone().date(1) : m.clone().add(1, 'month').date(1));
}

/**
 * Pay periods around today, oldest first, as 'YYYY-MM-DD' strings.
 * Built by walking the same 1st/16th steps the payment page uses, so the list can
 * never drift from the periods that page renders.
 */
export function getPaymentPeriods(back = 6, forward = 6) {
  const periods = [];
  let cursor = moment(getDefaultPaymentDate());
  for (let i = 0; i < back; i++) cursor = stepPaymentDate(cursor, -1);
  for (let i = 0; i <= back + forward; i++) {
    periods.push(cursor.format('YYYY-MM-DD'));
    cursor = stepPaymentDate(cursor, 1);
  }
  return periods;
}
